import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS_STYLE = {
  "ON JOB": "border-[#FF3B30]/50 bg-[#FF3B30]/15 text-[#FF5247]",
  AVAILABLE: "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]",
  OFFLINE: "border-zinc-600 bg-zinc-500/10 text-zinc-500",
};
const NEXT_STATUS = { AVAILABLE: "ON JOB", "ON JOB": "OFFLINE", OFFLINE: "AVAILABLE" };

export default function StationsBoard() {
  const [stations, setStations] = useState(null);
  const [addingTo, setAddingTo] = useState(null);
  const [newCallsign, setNewCallsign] = useState("");

  const load = () =>
    api
      .get("/stations/appliances")
      .then((r) => setStations(r.data.stations))
      .catch(() => setStations([]));

  useEffect(() => {
    load();
  }, []);

  const cycle = async (code, a) => {
    try {
      await api.put(`/stations/${code}/appliances`, {
        callsign: a.callsign,
        name: a.name,
        status: NEXT_STATUS[a.status] || "AVAILABLE",
      });
      load();
    } catch (e) {
      toast.error("Could not update appliance");
    }
  };

  const remove = async (code, callsign) => {
    try {
      await api.delete(`/stations/${code}/appliances/${callsign}`);
      toast.success(`${callsign} removed`);
      load();
    } catch (e) {
      toast.error("Could not remove appliance");
    }
  };

  const add = async (code) => {
    const cs = newCallsign.trim().toUpperCase();
    if (!cs) return;
    try {
      await api.put(`/stations/${code}/appliances`, { callsign: cs, status: "AVAILABLE" });
      setNewCallsign("");
      setAddingTo(null);
      toast.success(`${cs} added to ${code}`);
      load();
    } catch (e) {
      toast.error("Invalid callsign — letters/numbers, 2-10 chars");
    }
  };

  if (stations === null) {
    return (
      <p
        data-testid="stations-board-loading"
        className="py-10 text-center font-jb text-[11px] tracking-[0.3em] text-zinc-600"
      >
        READING PAGER TRAFFIC…
      </p>
    );
  }

  return (
    <div data-testid="stations-board">
      <p className="mb-4 font-jb text-[9px] leading-relaxed tracking-[0.25em] text-zinc-600">
        OFFICIAL FRV FLEET PER STATION · COLOURED CHIPS = ADDED BY CREW —
        CLICK A CHIP TO CYCLE STATUS, X TO REMOVE A CREW ENTRY
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {stations.map((s) => (
          <div
            key={s.code}
            data-testid={`station-board-${s.code}`}
            className="border border-[#27272A] bg-[#0D0D0D] p-5"
          >
            <div className="flex items-center justify-between">
              <span className="bg-[#FF6B00]/15 px-2 py-0.5 font-jb text-[11px] font-bold text-[#FF6B00]">
                {s.code}
              </span>
              <span className="font-jb text-[9px] tracking-[0.25em] text-zinc-600">
                FRV
              </span>
            </div>
            <p className="mt-3 font-chivo text-base font-bold tracking-tight text-white">
              {s.name}
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {s.appliances.length === 0 && (
                <span className="font-jb text-[10px] tracking-[0.15em] text-zinc-600">
                  NO APPLIANCES YET
                </span>
              )}
              {s.appliances.map((a) =>
                a.source === "manual" ? (
                  <span
                    key={a.callsign}
                    className={`flex items-center gap-1 border px-1.5 py-0.5 font-jb text-[10px] ${STATUS_STYLE[a.status] || STATUS_STYLE.AVAILABLE}`}
                  >
                    <button
                      data-testid={`cycle-${s.code}-${a.callsign}`}
                      onClick={() => cycle(s.code, a)}
                      title={`${a.name} — ${a.status} · click to change status`}
                      className="font-bold"
                    >
                      {a.callsign}
                    </button>
                    <button
                      data-testid={`remove-${s.code}-${a.callsign}`}
                      onClick={() => remove(s.code, a.callsign)}
                      aria-label={`Remove ${a.callsign}`}
                      className="opacity-50 transition-opacity hover:opacity-100"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ) : (
                  <span
                    key={a.callsign}
                    title={
                      a.incident
                        ? `${a.name} — on job ${a.incident.code} (${a.incident.type})`
                        : a.name
                    }
                    className={`border px-2 py-0.5 font-jb text-[10px] ${
                      a.status === "ON JOB"
                        ? "border-[#FF3B30]/50 bg-[#FF3B30]/15 font-bold text-[#FF5247]"
                        : "border-[#27272A] bg-black/40 text-zinc-500"
                    }`}
                  >
                    {a.callsign}
                    {a.status === "ON JOB" ? " ▲" : ""}
                  </span>
                )
              )}
            </div>
            {addingTo === s.code ? (
              <div className="mt-3 flex gap-1.5">
                <input
                  data-testid={`add-input-${s.code}`}
                  value={newCallsign}
                  onChange={(e) => setNewCallsign(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && add(s.code)}
                  placeholder="CALLSIGN E.G. P38A"
                  maxLength={10}
                  className="w-full border border-[#27272A] bg-black/40 px-3 py-1.5 font-jb text-[10px] tracking-[0.15em] text-white placeholder:text-zinc-600 focus:border-[#FF3B30] focus:outline-none"
                />
                <button
                  data-testid={`add-confirm-${s.code}`}
                  onClick={() => add(s.code)}
                  className="shrink-0 bg-[#FF3B30] px-3 font-jb text-[10px] font-bold text-black transition-colors hover:bg-[#FFC107]"
                >
                  ADD
                </button>
                <button
                  onClick={() => {
                    setAddingTo(null);
                    setNewCallsign("");
                  }}
                  aria-label="Cancel add"
                  className="shrink-0 border border-[#27272A] px-2 text-zinc-500 transition-colors hover:text-white"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                data-testid={`add-appliance-${s.code}`}
                onClick={() => {
                  setAddingTo(s.code);
                  setNewCallsign("");
                }}
                className="mt-3 flex items-center gap-1.5 border border-dashed border-zinc-700 px-3 py-1.5 font-jb text-[9px] tracking-[0.2em] text-zinc-500 transition-colors duration-300 hover:border-[#FF3B30] hover:text-white"
              >
                <Plus className="h-3 w-3" />
                ADD APPLIANCE
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

